import { create } from "zustand";
import { toast } from "sonner";
import type { EveAuthMode, EveIntegration } from "@/types/eve";

/**
 * Settings → Integrations: the org's Composio connected accounts (the Gmail
 * mailbox pool Amy sends from + any other toolkit). Reads/writes ride the
 * amy action routes with the Clerk bearer (org comes from the token).
 * `canRemove` / `canConnectAny` are the SERVER's verdicts (org admin /
 * Uplodio staff) — the routes re-check both on every write, so the UI
 * gating is cosmetic, not the boundary.
 */

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

interface IntegrationsState {
  connections: EveIntegration[];
  /** The curated toolkit list the server offers every member. */
  toolkits: string[];
  canRemove: boolean;
  canConnectAny: boolean;
  isLoading: boolean;
  /** true once a fetch has succeeded (distinguishes "empty" from "never loaded"). */
  loaded: boolean;
  /** true when the last fetch failed (network or non-ok) — the section shows its retry copy. */
  loadError: boolean;
  /** Toolkit slug whose connect link is being minted. */
  connecting: string | null;
  /** Connected-account id being removed. */
  removing: string | null;
  fetchIntegrations: (token: string) => Promise<void>;
  /**
   * Returns the Composio Connect Link URL to send the browser to, or null.
   * `authMode` picks the OAuth app (Composio's by default, or Uplodio's own
   * client) — the picker offers it for Gmail.
   */
  createConnectLink: (toolkit: string, token: string, authMode?: EveAuthMode) => Promise<string | null>;
  removeIntegration: (connectedAccountId: string, token: string) => Promise<boolean>;
}

async function post<T extends { ok: boolean; error?: string }>(
  path: string,
  body: Record<string, unknown>,
  token: string
): Promise<T | null> {
  try {
    const response = await fetch(`${EVE_HOST}/eve/v1/actions/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export const useIntegrationsStore = create<IntegrationsState>((set, get) => ({
  connections: [],
  toolkits: [],
  canRemove: false,
  canConnectAny: false,
  isLoading: false,
  loaded: false,
  loadError: false,
  connecting: null,
  removing: null,

  fetchIntegrations: async (token) => {
    set({ isLoading: true, loadError: false });
    const data = await post<{
      ok: boolean;
      error?: string;
      connections?: EveIntegration[];
      toolkits?: string[];
      can_remove?: boolean;
      can_connect_any?: boolean;
    }>("list-integrations", {}, token);
    if (!data?.ok) {
      // No toast (funding-store precedent) — the section renders its retry copy.
      set({ isLoading: false, loadError: true });
      return;
    }
    set({
      connections: data.connections ?? [],
      toolkits: data.toolkits ?? [],
      canRemove: data.can_remove ?? false,
      canConnectAny: data.can_connect_any ?? false,
      isLoading: false,
      loaded: true,
    });
  },

  createConnectLink: async (toolkit, token, authMode) => {
    set({ connecting: toolkit });
    try {
      const data = await post<{ ok: boolean; error?: string; connect_url?: string }>(
        "create-connect-link",
        { toolkit, ...(authMode ? { auth_mode: authMode } : {}) },
        token
      );
      if (!data) {
        toast.error("Could not reach Amy — try again in a moment.");
        return null;
      }
      if (!data.ok || !data.connect_url) {
        toast.error(data.error ?? "Couldn't create the connect link.");
        return null;
      }
      return data.connect_url;
    } finally {
      set({ connecting: null });
    }
  },

  removeIntegration: async (connectedAccountId, token) => {
    set({ removing: connectedAccountId });
    try {
      const data = await post<{ ok: boolean; error?: string; detail?: string }>(
        "remove-integration",
        { connected_account_id: connectedAccountId },
        token
      );
      if (!data) {
        toast.error("Could not reach Amy — try again in a moment.");
        return false;
      }
      if (!data.ok) {
        // 409 in_use / 403 admin-only / 502 composio — the server's text is user-grade.
        toast.error(data.error ?? "Couldn't remove the connection.");
        return false;
      }
      toast.success(data.detail ?? "Connection removed.");
      await get().fetchIntegrations(token);
      return true;
    } finally {
      set({ removing: null });
    }
  },
}));
